import { useAppDispatch, useAppSelector } from 'app/hooks';
import PromptParameter from 'components/admin/prompt-parameter';
import TextEditor from 'components/admin/text-editor';
import Button from 'components/inputs/button';
import Select from 'components/inputs/select';
import { PromptType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useState } from 'react';
import axios from 'utils/api';
import { confirmationModal, errorModal, successModal } from 'utils/ui';

export default function PromptsPage() {
  const dispatch = useAppDispatch();
  const { prompts } = useAppSelector((state) => state.admin);
  const { user } = useAppSelector((state) => state.auth);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptType | null>(null);
  const [selectedPromptVersion, setSelectedPromptVersion] = useState<
    number | null
  >(null);
  const [activeVersion, setActiveVersion] = useState<number | null>(null);
  const [content, setContent] = useState('');
  const [modelPreference, setModelPreference] = useState('balanced');
  const [versionHistory, setVersionHistory] = useState<PromptType[]>([]);

  const handleVersionSelect = (version: number | string) => {
    const newPrompt = versionHistory.find((v) => v.version === Number(version));
    if (newPrompt) {
      setSelectedPrompt(newPrompt);
    }
  };

  const updateVersionHistory = async (selectLatest = false) => {
    if (selectedPrompt) {
      const newVersionHistory = (
        await axios.get(`prompt/version-history?code=${selectedPrompt.code}`)
      ).data as PromptType[];
      setVersionHistory(newVersionHistory);
      if (selectLatest) {
        const newPrompt = newVersionHistory[newVersionHistory.length - 1];
        setSelectedPrompt(newPrompt);
      }
    }
  };

  useEffect(() => {
    if (selectedPrompt) {
      setSelectedPromptVersion(selectedPrompt.version);
      setActiveVersion(
        prompts.find((p) => p.code === selectedPrompt.code)?.version ?? null
      );
      setContent(selectedPrompt.content);
      setModelPreference(selectedPrompt.modelPreference);
      updateVersionHistory();
    } else {
      setSelectedPromptVersion(null);
      setActiveVersion(null);
      setContent('');
      setModelPreference('balanced');
      setVersionHistory([]);
    }
  }, [prompts, selectedPrompt]);

  const handleSave = async () => {
    try {
      await axios.patch('prompt', {
        code: selectedPrompt?.code,
        content,
        modelPreference,
        user: user?.name ?? 'Admin'
      });
      dispatch(fetchDefaultAdminData());
      updateVersionHistory(true);
      successModal(`New version of ${selectedPrompt?.label} created.`);
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    confirmationModal(
      'Are you sure you want to reset this prompt? All your changes will be lost!',
      () => {
        handleVersionSelect(selectedPromptVersion ?? 0);
      }
    );
  };

  const handleActivate = async () => {
    try {
      await axios.post('prompt/activate', {
        code: selectedPrompt?.code,
        version: selectedPromptVersion,
        user: user?.name ?? 'Admin'
      });
      dispatch(fetchDefaultAdminData());
      successModal(
        `${selectedPrompt?.label} v${selectedPromptVersion} activated.`
      );
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const getVersionLabel = (version: number) => {
    const prompt = versionHistory.find((v) => v.version === version);
    if (prompt) {
      let label = `v${prompt.version.toString()}`;
      if (prompt.latestVersion === version) label += ' (Latest)';
      if (prompt.version === activeVersion) label += ' (Active)';
      return label;
    }
    return version.toString();
  };

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>
        AI Prompt Management
      </div>
      <div className='flex gap-5'>
        <div className='flex w-[300px] shrink-0 flex-col gap-3'>
          <div className='font-semibold text-gray-900'>Prompt</div>
          <Select
            onChange={(value) =>
              setSelectedPrompt(
                value !== ''
                  ? prompts.find((p) => p.code === value) ?? null
                  : null
              )
            }
            value={selectedPrompt?.code ?? undefined}
            options={prompts.map((p) => ({
              label: p.label,
              value: p.code
            }))}
          />
          <div className='font-semibold text-gray-900'>Version</div>
          <Select
            onChange={(value) => handleVersionSelect(value)}
            value={selectedPromptVersion?.toString() ?? undefined}
            options={[
              ...versionHistory.map((v) => ({
                label: getVersionLabel(v.version),
                value: v.version
              }))
            ]}
            disabled={selectedPrompt === null}
            preventDeselection={selectedPrompt !== null}
          />
          {selectedPrompt && (
            <>
              <div className='font-semibold text-gray-900'>
                Model Preference
              </div>
              <Select
                onChange={(value) => setModelPreference(value.toString())}
                value={modelPreference}
                options={[
                  { label: 'Fast', value: 'fast' },
                  { label: 'Balanced', value: 'balanced' },
                  { label: 'Smart', value: 'smart' }
                ]}
                disabled={selectedPrompt === null}
                preventDeselection
              />
              <div className='font-semibold text-gray-900'>Parameters</div>
              <div className='flex flex-wrap gap-2'>
                {selectedPrompt?.parameters.map((p) => (
                  <PromptParameter
                    key={p}
                    parameter={p}
                    completed={content.includes(`{{${p}}}`)}
                  />
                ))}
                {selectedPrompt?.parameters.length === 0 && (
                  <span className=' text-gray-500'>None</span>
                )}
              </div>
              <div className='font-semibold text-gray-900'>Author</div>
              <div className='text-gray-500'>{selectedPrompt?.createdBy}</div>
              <div className='font-semibold text-gray-900'>Created</div>
              <div className='text-gray-500'>
                {new Date(selectedPrompt?.createdAt ?? '').toLocaleString()}
              </div>
              <div className='font-semibold text-gray-900'>Response Format</div>
              <div
                className={`text-gray-500 ${
                  selectedPrompt?.returnFormat === 'json'
                    ? 'uppercase'
                    : 'capitalize'
                }`}
              >
                {selectedPrompt?.returnFormat}
              </div>
            </>
          )}
        </div>
        <div className='flex grow flex-col gap-5'>
          <div className='h-[70vh] w-full rounded-md border-gray-300 bg-white shadow-sm'>
            <TextEditor
              defaultValue={selectedPrompt?.content}
              onChange={(value) => setContent(value)}
              wordsToHighlight={selectedPrompt?.parameters}
              disabled={selectedPrompt === null}
            />
          </div>
          <div className='flex items-center gap-3'>
            <Button
              text='Save As New Version'
              color='green'
              onClick={handleSave}
              disabled={
                selectedPrompt === null ||
                content === '' ||
                (content === selectedPrompt.content &&
                  modelPreference === selectedPrompt.modelPreference)
              }
            />
            <Button
              text='Reset'
              color='red'
              onClick={handleReset}
              disabled={
                selectedPrompt === null ||
                (content === selectedPrompt.content &&
                  modelPreference === selectedPrompt.modelPreference)
              }
            />
            <div className='ml-auto'>
              <Button
                text={
                  selectedPromptVersion === activeVersion
                    ? 'Active'
                    : 'Activate This Version'
                }
                color='secondary'
                onClick={handleActivate}
                disabled={
                  selectedPrompt === null ||
                  selectedPromptVersion === null ||
                  selectedPromptVersion === activeVersion ||
                  content !== selectedPrompt.content ||
                  modelPreference !== selectedPrompt.modelPreference
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
